import React, { Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { ProtectedAdminRoute } from "@/components/ProtectedAdminRoute";
import { UpdateBanner, useUpdateNotification } from "@/components/UpdateBanner";
import { UPDATE_AVAILABLE_EVENT } from "@/constants/events";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { usePasswordRecoveryDetection } from "@/hooks/usePasswordRecoveryDetection";

// Lazy load all page components for code splitting
const Index = React.lazy(() => import("./pages/Index"));
const Auth = React.lazy(() => import("./pages/Auth"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const SignAgreement = React.lazy(() => import("./pages/SignAgreement"));
const GuardianSignAgreement = React.lazy(() => import("./pages/GuardianSignAgreement"));
const SignatureComplete = React.lazy(() => import("./pages/SignatureComplete"));
const AdminAgreements = React.lazy(() => import("./pages/AdminAgreements"));
const AdminDocuments = React.lazy(() => import("./pages/AdminDocuments"));
const AdminEmailLogs = React.lazy(() => import("./pages/AdminEmailLogs"));
const AdminReviewers = React.lazy(() => import("./pages/AdminReviewers"));
const AdminReviewAssignments = React.lazy(() => import("./pages/AdminReviewAssignments"));
const AdminContentAnonymization = React.lazy(() => import("./pages/AdminContentAnonymization"));
const AdminScoringDashboard = React.lazy(() => import("./pages/AdminScoringDashboard"));
const AdminAICalibrationDashboard = React.lazy(() => import("./pages/AdminAICalibrationDashboard"));
const AdminReviewComments = React.lazy(() => import("./pages/AdminReviewComments"));
const AdminBlindingSettings = React.lazy(() => import("./pages/AdminBlindingSettings"));
const AdminReviewerGroups = React.lazy(() => import("./pages/AdminReviewerGroups"));
const AdminSetup = React.lazy(() => import("./pages/AdminSetup"));
const ReviewerApplicationReview = React.lazy(() => import("./pages/ReviewerApplicationReview"));
const ReviewerDashboard = React.lazy(() => import("./pages/ReviewerDashboard"));
const ReviewerAuth = React.lazy(() => import("./pages/ReviewerAuth"));
const AcceptReviewerInvitation = React.lazy(() => import("./pages/AcceptReviewerInvitation"));
const AdminLogin = React.lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));
const AdminUsers = React.lazy(() => import("./pages/AdminUsers"));
const AdminReports = React.lazy(() => import("./pages/AdminReports"));
const AdminDetailedReports = React.lazy(() => import("./pages/AdminDetailedReports"));
const AdminSettings = React.lazy(() => import("./pages/AdminSettings"));
const AdminWorkshops = React.lazy(() => import("./pages/AdminWorkshops"));
const AdminReminders = React.lazy(() => import("./pages/AdminReminders"));
const AdminReminderReports = React.lazy(() => import("./pages/AdminReminderReports"));
const ApplicationWorkshops = React.lazy(() => import("./pages/ApplicationWorkshops"));
const AdminDocumentManagement = React.lazy(() => import("./pages/AdminDocumentManagement"));
const AdminFraudDetection = React.lazy(() => import("./pages/AdminFraudDetection"));
const AdminPasswordResetMonitoring = React.lazy(() => import("./pages/AdminPasswordResetMonitoring"));
const AdminSupport = React.lazy(() => import("./pages/AdminSupport"));
const AdminSupportTickets = React.lazy(() => import("./pages/AdminSupportTickets"));
const EventAnalyticsDashboard = React.lazy(() => import("@/components/EventAnalyticsDashboard").then(module => ({ default: module.EventAnalyticsDashboard })));
const AdminApplications = React.lazy(() => import("./pages/AdminApplications"));
const AdminApplicationView = React.lazy(() => import("./pages/AdminApplicationView"));
const AdminApplicationEdit = React.lazy(() => import("./pages/AdminApplicationEdit"));
const AdminApplicationMessages = React.lazy(() => import("./pages/AdminApplicationMessages"));
const AdminApplicationImpersonate = React.lazy(() => import("./pages/AdminApplicationImpersonate"));
const ApplicationDashboard = React.lazy(() => import("./pages/ApplicationDashboard"));
const ApplicationCountyEligibility = React.lazy(() => import("./pages/ApplicationCountyEligibility"));
const ApplicationRequirements = React.lazy(() => import("./pages/ApplicationRequirements"));
const ApplicationEligibility = React.lazy(() => import("./pages/ApplicationEligibility"));
const ApplicationDocuments = React.lazy(() => import("./pages/ApplicationDocuments"));
const ApplicationContact = React.lazy(() => import("./pages/ApplicationContact"));
const ApplicationHousehold = React.lazy(() => import("./pages/ApplicationHousehold"));
const ApplicationFinancial = React.lazy(() => import("./pages/ApplicationFinancial"));
const ApplicationEducation = React.lazy(() => import("./pages/ApplicationEducation"));
const ApplicationEssayService = React.lazy(() => import("./pages/ApplicationEssayService"));
const ApplicationScholarship = React.lazy(() => import("./pages/ApplicationScholarship"));
const ApplicationRecommendations = React.lazy(() => import("./pages/ApplicationRecommendations"));
const ApplicationCommitments = React.lazy(() => import("./pages/ApplicationCommitments"));
const ApplicationReview = React.lazy(() => import("./pages/ApplicationReview"));
const ApplicationStatisticsReport = React.lazy(() => import("./pages/ApplicationStatisticsReport"));
const ReviewProgressReport = React.lazy(() => import("./pages/ReviewProgressReport"));
const DemographicAnalysisReport = React.lazy(() => import("./pages/DemographicAnalysisReport"));
const FinancialDistributionReport = React.lazy(() => import("./pages/FinancialDistributionReport"));
const AdminDemographicsExport = React.lazy(() => import("./pages/AdminDemographicsExport"));
const IncompleteApplicationsReport = React.lazy(() => import("./pages/IncompleteApplicationsReport"));
const DonorImpactReport = React.lazy(() => import("./pages/DonorImpactReport"));
const SubmitRecommendation = React.lazy(() => import("./pages/SubmitRecommendation"));
const StudentDashboard = React.lazy(() => import("./pages/StudentDashboard"));
const AdminDeclinedAgreements = React.lazy(() => import("./pages/AdminDeclinedAgreements"));
const AdminEligibilityReport = React.lazy(() => import("./pages/AdminEligibilityReport"));
const AdminEligibilityEmailPreview = React.lazy(() => import("./pages/AdminEligibilityEmailPreview"));
const Help = React.lazy(() => import("./pages/Help"));
const DownloadPresentation = React.lazy(() => import("./pages/DownloadPresentation"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const AdminChatKnowledgeBase = React.lazy(() => import("./pages/AdminChatKnowledgeBase"));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Component that handles password recovery detection at app level
const PasswordRecoveryHandler = ({ children }: { children: React.ReactNode }) => {
  usePasswordRecoveryDetection();
  return <>{children}</>;
};

const queryClient = new QueryClient();

const App = () => {
  const { showUpdate, handleUpdate, handleDismiss, triggerUpdateNotification } = useUpdateNotification();
  useNetworkStatus();

  useEffect(() => {
    const handleUpdateAvailable = () => {
      triggerUpdateNotification();
    };

    window.addEventListener(UPDATE_AVAILABLE_EVENT, handleUpdateAvailable);
    
    return () => {
      window.removeEventListener(UPDATE_AVAILABLE_EVENT, handleUpdateAvailable);
    };
  }, [triggerUpdateNotification]);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        {showUpdate && <UpdateBanner onUpdate={handleUpdate} onDismiss={handleDismiss} />}
        <BrowserRouter>
          <PasswordRecoveryHandler>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/reviewer-auth" element={<ReviewerAuth />} />
              <Route path="/admin-login" element={<AdminLogin />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/sign-agreement/:id" element={<SignAgreement />} />
              <Route path="/sign-agreement" element={<SignAgreement />} />
              <Route path="/guardian-sign-agreement" element={<GuardianSignAgreement />} />
              <Route path="/signature-complete" element={<SignatureComplete />} />
               <Route path="/admin" element={<AdminLogin />} />
               <Route path="/admin/setup" element={<AdminSetup />} />
               <Route path="/admin/dashboard" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
               <Route path="/admin/users" element={<ProtectedAdminRoute><AdminUsers /></ProtectedAdminRoute>} />
               <Route path="/admin/reports" element={<ProtectedAdminRoute><AdminReports /></ProtectedAdminRoute>} />
               <Route path="/admin/reports/application-statistics" element={<ProtectedAdminRoute><ApplicationStatisticsReport /></ProtectedAdminRoute>} />
               <Route path="/admin/reports/review-progress" element={<ProtectedAdminRoute><ReviewProgressReport /></ProtectedAdminRoute>} />
               <Route path="/admin/reports/demographic-analysis" element={<ProtectedAdminRoute><DemographicAnalysisReport /></ProtectedAdminRoute>} />
               <Route path="/admin/reports/financial-distribution" element={<ProtectedAdminRoute><FinancialDistributionReport /></ProtectedAdminRoute>} />
               <Route path="/admin/reports/demographics-export" element={<ProtectedAdminRoute><AdminDemographicsExport /></ProtectedAdminRoute>} />
               <Route path="/admin/reports/incomplete-applications" element={<ProtectedAdminRoute><IncompleteApplicationsReport /></ProtectedAdminRoute>} />
               <Route path="/admin/reports/donor-impact" element={<ProtectedAdminRoute><DonorImpactReport /></ProtectedAdminRoute>} />
               <Route path="/admin/reports/eligibility" element={<ProtectedAdminRoute><AdminEligibilityReport /></ProtectedAdminRoute>} />
               <Route path="/admin/detailed-reports" element={<ProtectedAdminRoute><AdminDetailedReports /></ProtectedAdminRoute>} />
               <Route path="/admin/settings" element={<ProtectedAdminRoute><AdminSettings /></ProtectedAdminRoute>} />
               <Route path="/admin/settings/email-preview" element={<ProtectedAdminRoute><AdminEligibilityEmailPreview /></ProtectedAdminRoute>} />
               <Route path="/admin/workshops" element={<ProtectedAdminRoute><AdminWorkshops /></ProtectedAdminRoute>} />
               <Route path="/admin/event-analytics" element={<ProtectedAdminRoute><EventAnalyticsDashboard /></ProtectedAdminRoute>} />
               <Route path="/admin/document-management" element={<ProtectedAdminRoute><AdminDocumentManagement /></ProtectedAdminRoute>} />
               <Route path="/admin/reminders" element={<ProtectedAdminRoute><AdminReminders /></ProtectedAdminRoute>} />
               <Route path="/admin/reminder-reports" element={<ProtectedAdminRoute><AdminReminderReports /></ProtectedAdminRoute>} />
               <Route path="/admin/password-reset-monitoring" element={<ProtectedAdminRoute><AdminPasswordResetMonitoring /></ProtectedAdminRoute>} />
               <Route path="/admin/fraud-detection" element={<ProtectedAdminRoute><AdminFraudDetection /></ProtectedAdminRoute>} />
               <Route path="/admin/declined-agreements" element={<ProtectedAdminRoute><AdminDeclinedAgreements /></ProtectedAdminRoute>} />
               <Route path="/admin/agreements" element={<ProtectedAdminRoute><AdminAgreements /></ProtectedAdminRoute>} />
              <Route path="/admin/documents" element={<ProtectedAdminRoute><AdminDocuments /></ProtectedAdminRoute>} />
              <Route path="/admin/email-logs" element={<ProtectedAdminRoute><AdminEmailLogs /></ProtectedAdminRoute>} />
              <Route path="/admin/reviewers" element={<ProtectedAdminRoute><AdminReviewers /></ProtectedAdminRoute>} />
              <Route path="/admin/review-assignments" element={<ProtectedAdminRoute><AdminReviewAssignments /></ProtectedAdminRoute>} />
               <Route path="/admin/anonymization" element={<ProtectedAdminRoute><AdminContentAnonymization /></ProtectedAdminRoute>} />
               <Route path="/admin/scoring-dashboard" element={<ProtectedAdminRoute><AdminScoringDashboard /></ProtectedAdminRoute>} />
               <Route path="/admin/ai-calibration" element={<ProtectedAdminRoute><AdminAICalibrationDashboard /></ProtectedAdminRoute>} />
               <Route path="/admin/review-comments" element={<ProtectedAdminRoute><AdminReviewComments /></ProtectedAdminRoute>} />
               <Route path="/admin/blinding-settings" element={<ProtectedAdminRoute><AdminBlindingSettings /></ProtectedAdminRoute>} />
               <Route path="/admin/reviewer-groups" element={<ProtectedAdminRoute><AdminReviewerGroups /></ProtectedAdminRoute>} />
               <Route path="/admin/support" element={<ProtectedAdminRoute><AdminSupport /></ProtectedAdminRoute>} />
               <Route path="/admin/support-tickets" element={<ProtectedAdminRoute><AdminSupportTickets /></ProtectedAdminRoute>} />
               <Route path="/admin/chat-knowledge-base" element={<ProtectedAdminRoute><AdminChatKnowledgeBase /></ProtectedAdminRoute>} />
               {/* Reviewer routes - redirect /reviewer to /reviewer/auth */}
               <Route path="/reviewer" element={<Navigate to="/reviewer/auth" replace />} />
               <Route path="/reviewer/auth" element={<ReviewerAuth />} />
               <Route path="/reviewer/accept-invitation" element={<AcceptReviewerInvitation />} />
               <Route path="/reviewer/review/:assignmentId" element={<ReviewerApplicationReview />} />
               <Route path="/reviewer/dashboard" element={<ReviewerDashboard />} />
              <Route path="/admin/applications" element={<ProtectedAdminRoute><AdminApplications /></ProtectedAdminRoute>} />
              <Route path="/admin/applications/:id/view" element={<ProtectedAdminRoute><AdminApplicationView /></ProtectedAdminRoute>} />
              <Route path="/admin/applications/:id/edit" element={<ProtectedAdminRoute><AdminApplicationEdit /></ProtectedAdminRoute>} />
              <Route path="/admin/applications/:id/messages" element={<ProtectedAdminRoute><AdminApplicationMessages /></ProtectedAdminRoute>} />
              <Route path="/admin/applications/:id/impersonate" element={<ProtectedAdminRoute><AdminApplicationImpersonate /></ProtectedAdminRoute>} />
              <Route path="/application" element={<ApplicationDashboard />} />
              <Route path="/application/county-eligibility" element={<ApplicationCountyEligibility />} />
              <Route path="/application/dashboard" element={<Navigate to="/application" replace />} />
              <Route path="/application/requirements" element={<ApplicationRequirements />} />
              <Route path="/application/eligibility" element={<ApplicationEligibility />} />
              <Route path="/application/documents" element={<ApplicationDocuments />} />
              <Route path="/application/contact" element={<ApplicationContact />} />
              <Route path="/application/household" element={<ApplicationHousehold />} />
              <Route path="/application/financial" element={<ApplicationFinancial />} />
              <Route path="/application/education" element={<ApplicationEducation />} />
              <Route path="/application/essay-service" element={<ApplicationEssayService />} />
              <Route path="/application/personal-statement" element={<Navigate to="/application/essay-service?tab=essay" replace />} />
              <Route path="/application/service" element={<Navigate to="/application/essay-service?tab=service" replace />} />
              <Route path="/application/scholarship" element={<ApplicationScholarship />} />
              <Route path="/application/recommendations" element={<ApplicationRecommendations />} />
               <Route path="/application/commitments" element={<ApplicationCommitments />} />
               <Route path="/application/workshops" element={<ApplicationWorkshops />} />
               <Route path="/application/review" element={<ApplicationReview />} />
              <Route path="/submit-recommendation" element={<SubmitRecommendation />} />
              <Route path="/students" element={<StudentDashboard />} />
              <Route path="/admin/students" element={<ProtectedAdminRoute><StudentDashboard /></ProtectedAdminRoute>} />
              <Route path="/help" element={<Help />} />
              <Route path="/download-presentation" element={<DownloadPresentation />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </Layout>
          </PasswordRecoveryHandler>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
